import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { api } from '@/services/api';
import type { ParsedElement } from '@/pages/ActualDocumentPage';

interface RedactionInfo {
  redid: number;
  reddate: string;
  statename: string;
  redcaption: string;
  actual: boolean;
}

interface Article {
  id: number;
  article_type: string;
  article_number: string;
  article_title: string;
  article_text: string;
  children?: Article[];
}

interface CompareModalProps {
  show: boolean;
  onHide: () => void;
  docId: string;
  currentRedid: string;
}

interface DiffResult {
  elements: DiffElementData[];
  changes: { added: number; removed: number; modified: number };
}

interface DiffElementData extends ParsedElement {
  fullContent: string;
}

export const RedactionCompareModal: React.FC<CompareModalProps> = ({
  show,
  onHide,
  docId,
  currentRedid,
}) => {
  const [redactions, setRedactions] = useState<RedactionInfo[]>([]);
  const [selectedRedid, setSelectedRedid] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingRedactions, setLoadingRedactions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [diffResult, setDiffResult] = useState<DiffResult | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');

  useEffect(() => {
    if (!show || !docId) return;
    setLoadingRedactions(true);
    setError(null);
    api.get(`/actual/documents/${docId}`)
      .then((res: any) => {
        setRedactions(res.redactions || []);
      })
      .catch((err: any) => {
        console.error('Failed to load redactions:', err);
        setError('Не удалось загрузить список редакций');
      })
      .finally(() => setLoadingRedactions(false));
  }, [show, docId]);

  const handleCompare = async () => {
    if (!selectedRedid) return;
    setLoading(true);
    setError(null);
    setDebugInfo('');

    try {
      let currentArticles: Article[] = [];
      let selectedArticles: Article[] = [];
      let debugLog: string[] = [];

      try {
        const currentRes = await api.get(`/actual/redactions/${currentRedid}/content`);
        const data = currentRes.data || currentRes;
        currentArticles = data.articles || [];
        debugLog.push(`Current redid ${currentRedid}: ${currentArticles.length} articles (keys: ${Object.keys(data).join(',')})`);
      } catch (e: any) {
        debugLog.push(`Current redid ${currentRedid}: content FAILED - ${e.message}`);
      }

      try {
        const selectedRes = await api.get(`/actual/redactions/${selectedRedid}/content`);
        const data = selectedRes.data || selectedRes;
        selectedArticles = data.articles || [];
        debugLog.push(`Selected redid ${selectedRedid}: ${selectedArticles.length} articles (keys: ${Object.keys(data).join(',')})`);
      } catch (e: any) {
        debugLog.push(`Selected redid ${selectedRedid}: content FAILED - ${e.message}`);
      }

      if (currentArticles.length > 0 && selectedArticles.length > 0) {
        debugLog.push('Using STRUCTURED diff');
        const result = computeStructuredDiff(currentArticles, selectedArticles);
        setDebugInfo(debugLog.join('\n'));
        setDiffResult(result);
        setLoading(false);
        return;
      }

      debugLog.push('Falling back to HTML diff');

      let currentHtml = '';
      let selectedHtml = '';

      try {
        const currentRes = await api.get(`/actual/redactions/${currentRedid}/html`);
        currentHtml = extractHtml(currentRes);
        debugLog.push(`Current HTML: ${currentHtml.length} chars`);
      } catch (e1: any) {
        try {
          const currentRes = await api.get(`/actual/redactions/${currentRedid}/redtext`);
          currentHtml = extractHtml(currentRes);
          debugLog.push(`Current redtext: ${currentHtml.length} chars`);
        } catch (e2: any) {
          throw new Error(`Не удалось загрузить текущую редакцию (${currentRedid})`);
        }
      }

      try {
        const selectedRes = await api.get(`/actual/redactions/${selectedRedid}/html`);
        selectedHtml = extractHtml(selectedRes);
        debugLog.push(`Selected HTML: ${selectedHtml.length} chars`);
      } catch (e1: any) {
        try {
          const selectedRes = await api.get(`/actual/redactions/${selectedRedid}/redtext`);
          selectedHtml = extractHtml(selectedRes);
          debugLog.push(`Selected redtext: ${selectedHtml.length} chars`);
        } catch (e2: any) {
          throw new Error(`Не удалось загрузить выбранную редакцию (${selectedRedid})`);
        }
      }

      if (!currentHtml || !selectedHtml) {
        throw new Error('Пустой ответ от сервера');
      }

      const clean1 = cleanHtmlText(currentHtml).substring(0, 200);
      const clean2 = cleanHtmlText(selectedHtml).substring(0, 200);
      debugLog.push(`Clean1 preview: ${clean1.replace(/\n/g, ' | ')}`);
      debugLog.push(`Clean2 preview: ${clean2.replace(/\n/g, ' | ')}`);

      const result = computeHtmlDiff(currentHtml, selectedHtml);
      debugLog.push(`Diff result: +${result.changes.added} -${result.changes.removed} ~${result.changes.modified}`);

      setDebugInfo(debugLog.join('\n'));
      setDiffResult(result);
    } catch (err: any) {
      console.error('Compare error:', err);
      setError(err?.message || 'Ошибка сравнения');
    } finally {
      setLoading(false);
    }
  };

  const availableRedactions = useMemo(() => {
    return redactions.filter((r) => String(r.redid) !== currentRedid);
  }, [redactions, currentRedid]);

  return (
    <Modal show={show} onHide={onHide} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-arrow-left-right me-2"></i>
          Сравнение редакций
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {debugInfo && (
          <Alert variant="info" className="mb-3">
            <pre style={{ fontSize: 11, margin: 0, whiteSpace: 'pre-wrap' }}>{debugInfo}</pre>
          </Alert>
        )}

        {!diffResult ? (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Текущая редакция</Form.Label>
              <Form.Control
                disabled
                value={redactions.find((r) => String(r.redid) === currentRedid)?.redcaption || `Редакция ${currentRedid}`}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Сравнить с</Form.Label>
              {loadingRedactions ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Form.Select
                  value={selectedRedid}
                  onChange={(e) => setSelectedRedid(e.target.value)}
                >
                  <option value="">Выберите редакцию...</option>
                  {availableRedactions.map((r) => (
                    <option key={r.redid} value={r.redid}>
                      {r.redcaption} ({new Date(r.reddate).toLocaleDateString('ru-RU')})
                      {r.actual ? ' — АКТУАЛЬНАЯ' : ''}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>

            <Button
              variant="primary"
              onClick={handleCompare}
              disabled={!selectedRedid || loading}
            >
              {loading ? (
                <><Spinner size="sm" className="me-1" />Сравнение...</>
              ) : (
                <><i className="bi bi-arrow-left-right me-1"></i>Сравнить</>
              )}
            </Button>
          </>
        ) : (
          <DiffView result={diffResult} onReset={() => { setDiffResult(null); setDebugInfo(''); }} />
        )}
      </Modal.Body>
    </Modal>
  );
};

const DiffView: React.FC<{ result: DiffResult; onReset: () => void }> = ({
  result,
  onReset,
}) => {
  const [filter, setFilter] = useState<'all' | 'added' | 'removed' | 'modified'>('all');

  const filtered = useMemo(() => {
    if (filter === 'all') return result.elements;
    return result.elements.filter((el) => el.type === filter);
  }, [result, filter]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2">
          <Badge bg="success">+{result.changes.added} добавлено</Badge>
          <Badge bg="danger">-{result.changes.removed} удалено</Badge>
          <Badge bg="warning" text="dark">~{result.changes.modified} изменено</Badge>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={onReset}>
          <i className="bi bi-arrow-left me-1"></i>Назад
        </Button>
      </div>

      <Form.Select
        size="sm"
        className="mb-3"
        value={filter}
        onChange={(e) => setFilter(e.target.value as any)}
      >
        <option value="all">Все изменения</option>
        <option value="added">Только добавленные</option>
        <option value="removed">Только удалённые</option>
        <option value="modified">Только изменённые</option>
      </Form.Select>

      <div className="diff-viewer" style={{ fontSize: 15, lineHeight: 1.6 }}>
        {filtered.map((el, idx) => (
          <DiffElement key={idx} element={el} />
        ))}
      </div>
    </>
  );
};

const DiffElement: React.FC<{ element: DiffElementData }> = ({ element }) => {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW_LIMIT = 2000;

  const style: React.CSSProperties = {
    padding: '6px 12px',
    margin: '2px 0',
    borderRadius: 4,
  };

  switch (element.type) {
    case 'added':
      style.backgroundColor = '#dcfce7';
      style.borderLeft = '3px solid #16a34a';
      break;
    case 'removed':
      style.backgroundColor = '#fee2e2';
      style.borderLeft = '3px solid #dc2626';
      style.textDecoration = 'line-through';
      break;
    case 'modified':
      style.backgroundColor = '#fef9c3';
      style.borderLeft = '3px solid #ca8a04';
      break;
    default:
      break;
  }

  // Check if full content is long enough to need expand button
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = element.fullContent;
  const textContent = tempDiv.textContent || tempDiv.innerText || '';
  const isLong = textContent.length > PREVIEW_LIMIT;

  // For display: use fullContent if expanded, otherwise truncated content
  const displayContent = expanded ? element.fullContent : element.content;

  return (
    <div style={style}>
      {element.num && <span style={{ fontWeight: 'bold', marginRight: 8 }}>{element.num}</span>}
      <div>
        <span dangerouslySetInnerHTML={{ __html: displayContent }} />
      </div>
      {isLong && (
        <Button
          variant="link"
          size="sm"
          onClick={() => setExpanded(!expanded)}
          style={{ padding: '2px 0', fontSize: 13, marginTop: 4 }}
        >
          <i className={`bi bi-${expanded ? 'chevron-up' : 'chevron-down'}`}></i>
          {expanded ? ' Свернуть' : ' Развернуть'}
        </Button>
      )}
    </div>
  );
};

function extractHtml(response: any): string {
  if (!response) return '';
  if (typeof response === 'string') {
    try {
      const parsed = JSON.parse(response);
      return extractHtml(parsed);
    } catch {
      return response;
    }
  }
  const data = response.data !== undefined ? response.data : response;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      if (parsed.html) return parsed.html;
      if (parsed.redtext) return parsed.redtext;
      return data;
    } catch {
      return data;
    }
  }
  if (data.html) return data.html;
  if (data.redtext) return data.redtext;
  return '';
}

function flattenArticles(articles: Article[], path: string = ''): Map<string, string> {
  const result = new Map<string, string>();
  for (const art of articles) {
    const titlePreview = art.article_title.substring(0, 60).replace(/\n/g, ' ');
    const currentPath = path
      ? `${path} > ${art.article_number}. ${titlePreview}`
      : `${art.article_number}. ${titlePreview}`;
    const text = (art.article_title + '\n' + (art.article_text || '')).trim();
    result.set(currentPath, text);
    if (art.children && art.children.length > 0) {
      const childMap = flattenArticles(art.children, currentPath);
      childMap.forEach((value, key) => result.set(key, value));
    }
  }
  return result;
}

function computeStructuredDiff(current: Article[], selected: Article[]): DiffResult {
  const currentMap = flattenArticles(current);
  const selectedMap = flattenArticles(selected);
  const elements: DiffElementData[] = [];
  let added = 0, removed = 0, modified = 0;

  for (const [path, text] of selectedMap) {
    if (!currentMap.has(path)) {
      const fullContent = `<strong>${escapeHtml(path)}</strong><br/>${escapeHtml(text)}`;
      elements.push({
        type: 'added' as any,
        content: fullContent,
        fullContent: fullContent,
        num: '+'
      });
      added++;
    } else if (currentMap.get(path) !== text) {
      const oldText = currentMap.get(path) || '';
      const fullContent = `<strong>${escapeHtml(path)}</strong><br/>
        <div style="color:#666;text-decoration:line-through;margin:4px 0;">${escapeHtml(oldText)}</div>
        <div style="color:#047857;margin:4px 0;">${escapeHtml(text)}</div>`;
      // Truncated preview for initial display - 2000 chars limit
      const previewContent = `<strong>${escapeHtml(path)}</strong><br/>
        <div style="color:#666;text-decoration:line-through;margin:4px 0;">${escapeHtml(oldText.substring(0, 2000))}${oldText.length > 2000 ? '...' : ''}</div>
        <div style="color:#047857;margin:4px 0;">${escapeHtml(text.substring(0, 2000))}${text.length > 2000 ? '...' : ''}</div>`;
      elements.push({
        type: 'modified' as any,
        content: previewContent,
        fullContent: fullContent,
        num: '~'
      });
      modified++;
    }
  }

  for (const [path, text] of currentMap) {
    if (!selectedMap.has(path)) {
      const fullContent = `<strong>${escapeHtml(path)}</strong><br/>${escapeHtml(text)}`;
      elements.push({
        type: 'removed' as any,
        content: fullContent,
        fullContent: fullContent,
        num: '-'
      });
      removed++;
    }
  }

  const typeOrder = { removed: 0, modified: 1, added: 2 };
  elements.sort((a, b) => (typeOrder[a.type as keyof typeof typeOrder] || 0) - (typeOrder[b.type as keyof typeof typeOrder] || 0));
  return { elements, changes: { added, removed, modified } };
}

function computeHtmlDiff(html1: string, html2: string): DiffResult {
  const text1 = cleanHtmlText(html1);
  const text2 = cleanHtmlText(html2);
  const lines1 = text1.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const lines2 = text2.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const normalizeLine = (line: string) => line.replace(/\s+/g, ' ').replace(/\d{1,2}\.\d{2}\.\d{4}/g, '').replace(/\d{4}-\d{2}-\d{2}/g, '').trim();
  const norm1 = lines1.map(normalizeLine);
  const norm2 = lines2.map(normalizeLine);
  const set1 = new Set(norm1);
  const set2 = new Set(norm2);
  const elements: DiffElementData[] = [];
  let added = 0, removed = 0, modified = 0;
  const allNorms = new Set([...norm1, ...norm2]);

  for (const norm of allNorms) {
    const in1 = set1.has(norm);
    const in2 = set2.has(norm);
    if (in1 && !in2) {
      const idx = norm1.indexOf(norm);
      const text = lines1[idx] || norm;
      elements.push({ type: 'removed' as any, content: escapeHtml(text), fullContent: escapeHtml(text), num: '-' });
      removed++;
    } else if (!in1 && in2) {
      const idx = norm2.indexOf(norm);
      const text = lines2[idx] || norm;
      elements.push({ type: 'added' as any, content: escapeHtml(text), fullContent: escapeHtml(text), num: '+' });
      added++;
    }
  }

  const orderMap = new Map(norm2.map((n, i) => [n, i]));
  elements.sort((a, b) => {
    const ia = orderMap.get(normalizeLine(a.content)) ?? Infinity;
    const ib = orderMap.get(normalizeLine(b.content)) ?? Infinity;
    return ia - ib;
  });
  return { elements, changes: { added, removed, modified } };
}

function cleanHtmlText(html: string): string {
  if (!html) return '';
  let cleaned = html;
  cleaned = cleaned.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '\n');
  cleaned = cleaned.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '\n');
  cleaned = cleaned.replace(/@\w+[^\{]*\{[\s\S]*?\}/gi, '\n');
  cleaned = cleaned.replace(/[.#]?[\w*-]+(?:\s*[>,+~]\s*[.#]?[\w*-]+)*\s*\{[\s\S]*?\}/gi, '\n');
  cleaned = cleaned.replace(/\s+style\s*=\s*"[^"]*"/gi, '');
  cleaned = cleaned.replace(/\s+class\s*=\s*"[^"]*"/gi, '');
  const tmp = document.createElement('div');
  tmp.innerHTML = cleaned;
  let text = tmp.textContent || tmp.innerText || '';
  text = text
    .replace(/\b(?:margin|padding|border|font-size|font-family|line-height|color|background|width|height|display|position|max-width|word-wrap|text-align|border-radius|background-color)\b[\s:]*[^\n]*/gi, '')
    .replace(/#[0-9a-fA-F]{3,8}\b/g, '')
    .replace(/(?:rgb|rgba|hsl|hsla)\([^)]*\)/gi, '')
    .replace(/\n\s*\n/g, '\n')
    .replace(/^\s+/gm, '')
    .trim();
  return text;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}